import React from 'react';
import type { LeadPipeline, LeadPipelineStage } from '../../../types';

export type CategoryChangeStageModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (stageId: string) => Promise<void>;
  categoryLabel: string;
  pipeline: LeadPipeline | null;
  /** Current open-stage label (if any) — used to suggest a matching target stage. */
  currentStageLabel?: string | null;
  isSaving?: boolean;
  error?: string | null;
};

export function CategoryChangeStageModal({
  open,
  onClose,
  onConfirm,
  categoryLabel,
  pipeline,
  currentStageLabel,
  isSaving = false,
  error = null,
}: CategoryChangeStageModalProps) {
  const stages = React.useMemo<LeadPipelineStage[]>(
    () =>
      pipeline
        ? [...pipeline.stages].sort((a, b) => a.sort_order - b.sort_order)
        : [],
    [pipeline],
  );

  const [selectedStageId, setSelectedStageId] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    const label = String(currentStageLabel ?? '').trim();
    const match = label ? stages.find((s) => s.label === label) : undefined;
    setSelectedStageId(match?.id ?? '');
  }, [open, stages, currentStageLabel]);

  if (!open) return null;

  const pipelineName = pipeline?.name?.trim() || 'Pipeline';
  const canConfirm = Boolean(selectedStageId) && stages.length > 0 && !isSaving;

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canConfirm || !selectedStageId) return;
    await onConfirm(selectedStageId);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSaving) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="category-change-stage-title"
        className="bg-app-surface rounded-xl shadow-xl border border-app-border p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="category-change-stage-title" className="text-lg font-semibold text-app-text mb-1">
          Phase wählen
        </h3>
        <p className="text-sm text-app-text-muted mb-4">
          Die Kategorie „{categoryLabel || '—'}“ nutzt die Pipeline „{pipelineName}“. Bitte wählen Sie
          die Phase für dieses Projekt.
        </p>

        {error ? (
          <p className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
            {error}
          </p>
        ) : null}

        {stages.length === 0 ? (
          <p className="mb-4 p-3 rounded-md bg-amber-50 text-amber-800 text-sm border border-amber-200">
            Die Pipeline der neuen Kategorie hat keine Phasen. Bitte legen Sie zuerst Phasen in den
            Einstellungen an.
          </p>
        ) : (
          <ul className="space-y-1.5 max-h-64 overflow-y-auto" role="listbox" aria-label="Phasen">
            {stages.map((stage) => {
              const selected = selectedStageId === stage.id;
              return (
                <li key={stage.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={isSaving}
                    onClick={() => setSelectedStageId(stage.id)}
                    className={`w-full h-9 px-3.5 text-sm rounded-md border text-left inline-flex items-center transition-colors ${
                      selected
                        ? 'border-pipedrive-blue bg-blue-50 text-app-text font-medium'
                        : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`mr-2.5 inline-flex h-3.5 w-3.5 shrink-0 rounded-full border ${
                        selected
                          ? 'border-pipedrive-blue bg-pipedrive-blue'
                          : 'border-app-border-strong bg-app-surface'
                      }`}
                      aria-hidden
                    />
                    {stage.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="inline-flex h-9 items-center px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover rounded-md disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={(e) => void handleConfirm(e)}
            disabled={!canConfirm}
            className="inline-flex h-9 items-center px-3.5 text-sm font-medium bg-pipedrive-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Speichert…' : 'Kategorie wechseln'}
          </button>
        </div>
      </div>
    </div>
  );
}
